import type { FormEventHandler, ReactNode, SyntheticEvent } from "react";
import { classNames } from "./internal/class-names";

export interface FormProps {
  /**
   * Accessible name. Required: every form in this application is addressable by
   * name in tests and by assistive technology.
   */
  readonly label: string;
  /** Called after `preventDefault`. Returning a promise is fine — it is not awaited. */
  readonly onSubmit: () => void;
  /** Rendered in a footer row, typically Buttons. */
  readonly actions?: ReactNode | undefined;
  readonly className?: string | undefined;
  /** Stable id for submit buttons rendered in a dialog footer. */
  readonly id?: string | undefined;
  /** Opts out of browser validation when validation is owned by the workflow. */
  readonly noValidate?: boolean | undefined;
  /** Optional invalid-event hook for workflows that reveal the containing step. */
  readonly onInvalidCapture?: FormEventHandler<HTMLFormElement> | undefined;
  readonly children: ReactNode;
}

/** Native form wrapper that owns `preventDefault` so views never repeat it. */
export function Form({ actions, children, className, id, label, noValidate = false, onInvalidCapture, onSubmit }: FormProps): ReactNode {
  function handleSubmit(event: SyntheticEvent<HTMLFormElement>): void {
    event.preventDefault();
    onSubmit();
  }

  return (
    <form aria-label={label} className={classNames("thw-form", className)} id={id} noValidate={noValidate} onInvalidCapture={onInvalidCapture} onSubmit={handleSubmit}>
      {children}
      {actions === undefined ? null : <div className="thw-form__actions">{actions}</div>}
    </form>
  );
}
